import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Play, Loader2, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { API, ax, errorText } from "./common";

const BADGE = {
  running: "bg-blue-100 text-blue-700",
  passed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
};

// Shown only where the backend enables the test runner (development/test environments).
export default function TestRunPanel({ showMsg }) {
  const { t } = useLang();
  const [run, setRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/test-run`, ax);
      setRun(data);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);

  const running = run?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [running, load]);

  const start = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/test-run`, {}, ax);
      setRun(data);
    } catch (e) {
      showMsg("error", e?.response?.status === 409 ? t("adm.tr.already") : errorText(e, t("adm.error")));
      load();
    }
  };

  const clear = async () => {
    try {
      const { data } = await axios.delete(`${API}/admin/test-run`, ax);
      setRun(data);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  // Keep the newest lines in view; the pytest summary is the last line.
  const outputRef = useRef(null);
  useEffect(() => {
    if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [run?.output]);

  if (!run?.enabled) return null;

  const lines = (run.output || "").split("\n");
  const summary = [...lines].reverse().find(l => /\d+ (passed|failed|error)/.test(l));
  const finished = run.finished_at ? new Date(run.finished_at * 1000).toLocaleString() : null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900 text-sm">{t("adm.tr.title")}</p>
          <p className="text-sm text-slate-600">{t("adm.tr.intro")}</p>
        </div>
        <Button size="sm" onClick={start} disabled={running} className="gap-1.5 bg-blue-600 hover:bg-blue-700 shrink-0">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {t("adm.tr.start")}
        </Button>
      </div>
      {run.status !== "idle" && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-block text-xs font-medium px-2 py-1 rounded-lg ${BADGE[run.status]}`}>{t(`adm.tr.${run.status}`)}</span>
              {summary && <span className="text-xs font-mono text-slate-700">{summary.replace(/=/g, "").trim()}</span>}
              {finished && <span className="text-xs text-slate-500">{finished}</span>}
            </div>
            {!running && (
              <Button size="sm" variant="outline" onClick={clear} className="gap-1.5">
                <X className="w-4 h-4" /> {t("adm.tr.clear")}
              </Button>
            )}
          </div>
          <pre ref={outputRef} className="max-h-96 overflow-auto rounded-xl bg-slate-900 text-slate-100 text-xs p-3 whitespace-pre-wrap">{run.output || "…"}</pre>
        </>
      )}
    </div>
  );
}
